// comms.js
import axios from 'axios';
import io from 'socket.io-client';
import { offlineStorage } from '../services/offlineStorage';
import { connectionManager } from '../services/connectionManager';

let socketConnection=null;
let outGoingMessageNumber = 1;

// Connection status management
let connectionStatus = 'disconnected';
let connectionStatusCallbacks = [];

// Active route tracking for automatic room rejoin on reconnection
let currentRouteId = null;

/** Optional handler: receives dispatch MSG text while Flag Trip awaits server feedback */
let flagTripAwaitingMessageHandler = null;

/**
 * After `completeWithPricing`, wait for inbound `PRICE|Ok|…` before sending `COMPM`.
 * If no ack arrives, fall back to COMPM after {@link PRICE_ACK_TIMEOUT_MS}.
 */
let pendingPriceComplete = null;
const PRICE_ACK_TIMEOUT_MS = 12_000;

function clearPendingPriceComplete() {
  if (pendingPriceComplete?.timeoutId) {
    clearTimeout(pendingPriceComplete.timeoutId);
  }
  pendingPriceComplete = null;
}

/** @returns {boolean} true if a pending completion was resolved and COMPM was sent */
function tryCompleteAfterPriceAck(ackTripToken) {
  if (!pendingPriceComplete) return false;
  const want = String(pendingPriceComplete.tripNumber).trim();
  const got = String(ackTripToken ?? '').trim();
  if (got && got !== want) return false;
  clearTimeout(pendingPriceComplete.timeoutId);
  const trip = pendingPriceComplete.tripNumber;
  clearPendingPriceComplete();
  comms.sendSocket(`COMPM|${trip}`, { skipOfflineQueue: true });
  return true;
}

// Route data persistence keys
const ROUTE_DATA_KEY = 'persistedRouteData';
const ROUTE_PERSIST_TIMESTAMP_KEY = 'routeDataPersistedAt';
const MAX_ROUTE_DATA_AGE_MS = 4 * 60 * 60 * 1000; // 4 hours

// Notify all subscribers of connection status change
const notifyConnectionStatus = (status) => {
    const previousStatus = connectionStatus;
    connectionStatus = status;
    connectionStatusCallbacks.forEach(cb => {
        try {
            cb(status, previousStatus);
        } catch (err) {
            console.error('Error in connection status callback:', err);
        }
    });
};

/** Transport-level failures when the host is down, wrong URL, or blocked — not app logic bugs */
const isBenignSocketTransportError = (error) => {
    if (!error) return false;
    const msg = String(error.message || error).toLowerCase();
    return (
        msg.includes('xhr poll error') ||
        msg.includes('xhr post error') ||
        msg.includes('websocket error') ||
        msg.includes('websocket is closed before the connection is established') ||
        msg.includes('failed to fetch') ||
        msg.includes('network error') ||
        msg.includes('load failed') ||
        msg.includes('connection refused')
    );
};

const comms = {
    authenticate: (driverNumber, pin, vehicleId = null) => {
        const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
        const req = {
            driverNumber,
            pin: pin.toString().padStart(4, "0"),
            unitId: fleetSettings.unitId,
            vehicleId: vehicleId
        };

        return axios.post(`${fleetSettings.fleetUrl}/API/Driver/Auth`, req)

            .then((res) => {
                if(res.error){
                    console.error('Authentication failed:', res.error);

                    throw res.error;
                }else{
                    localStorage.setItem('userSettings', JSON.stringify(res.data));
                    return res.data;
                }
            })
            .catch((err) => {
                console.error('Authentication failed:', err);

                throw err;
            });
    },
    
    // New simplified authorization method with offline support
    authorize: (driverNumber, pin, faceNumber = null, vehicleId = null, offlineMode = false) => {
        const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
        
        // If explicitly in offline mode, use cached credentials
        if (offlineMode) {
            const cachedUserSettings = localStorage.getItem('userSettings');
            const cachedAuthData = localStorage.getItem('lastAuthData');
            
            if (cachedUserSettings && cachedAuthData) {
                console.log('Using cached authorization (offline mode)');
                return Promise.resolve(JSON.parse(cachedAuthData));
            } else {
                return Promise.reject(new Error('No cached authorization data available'));
            }
        }
        
        const req = {
            driverNumber,
            faceNumber,
            pin: pin.toString().padStart(4, "0")
        };

        // Include vehicleId if provided (required when !fleetSettings.driverDevice)
        if (vehicleId) {
            req.vehicleId = vehicleId;
        }

        return axios.post(`${fleetSettings.fleetUrl}/API/Driver/Authorize`, req, {
            timeout: 10000 // 10 second timeout
        })
            .then((res) => {
                if (res.data.error) {
                    console.error('Authorization failed:', res.data.error);
                    throw new Error(res.data.error);
                } else {
                    // Store only essential user settings that survive reload
                    const userSettings = {
                        Id: res.data.driverId,
                        Code: driverNumber || faceNumber,
                        PIN: pin,
                        ...res.data.driverObject,
                        // Ensure UnitID is available for joinDriverAppRoom (server expects PascalCase UnitID)
                        // driverObject has camelCase unitId, dispatchData.vehicleObject has unitID
                        UnitID: res.data.driverObject?.unitId || res.data.driverObject?.UnitID || res.data.dispatchData?.vehicleObject?.unitID || res.data.dispatchData?.vehicleObject?.UnitID,
                        // Ensure VehicleID is available for inspection calls
                        // This is the VEHICLE's Id, not the driver's Id
                        VehicleID: res.data.dispatchData?.vehicleObject?.id || res.data.dispatchData?.vehicleObject?.Id || res.data.dispatchData?.vehicleId,
                        dispatchData: res.data.dispatchData || null,
                    };
                    localStorage.setItem('userSettings', JSON.stringify(userSettings));
                    
                    // Store complete auth data for offline recovery
                    localStorage.setItem('lastAuthData', JSON.stringify(res.data));
                    localStorage.setItem('lastAuthTimestamp', Date.now().toString());
                    
                    return res.data;
                }
            })
            .catch((err) => {
                console.error('Authorization error:', err);
                
                // Check if this is a 401 with explicit invalid credentials
                if (err.response?.status === 401 && 
                    err.response?.data?.error === 'Invalid credentials') {
                    // This is an explicit auth failure - don't use cache
                    throw new Error('Invalid credentials');
                }
                
                // For network errors or timeouts, try to use cached data
                const cachedUserSettings = localStorage.getItem('userSettings');
                const cachedAuthData = localStorage.getItem('lastAuthData');
                
                if (cachedUserSettings && cachedAuthData) {
                    const authData = JSON.parse(cachedAuthData);
                    const lastAuthTime = parseInt(localStorage.getItem('lastAuthTimestamp') || '0');
                    const hoursSinceAuth = (Date.now() - lastAuthTime) / (1000 * 60 * 60);
                    
                    // Use cached data if less than 24 hours old
                    if (hoursSinceAuth < 24) {
                        console.warn('Network error during auth - using cached data (last auth: ' + 
                            hoursSinceAuth.toFixed(1) + ' hours ago)');
                        return authData;
                    }
                }
                
                // No valid cache or cache too old
                throw err;
            });
    },
    
    getSettings: (fleetCode, useCache = false) => {
        const req = { unitId: fleetCode };
        
        // If explicitly using cache (offline mode), return cached settings
        if (useCache) {
            const cachedSettings = localStorage.getItem('fleetSettings');
            if (cachedSettings) {
                console.log('Using cached fleet settings (offline mode)');
                return Promise.resolve(JSON.parse(cachedSettings));
            }
        }
        
        // Get config server URL from window.APP_CONFIG (set in index.html)
        const configServerUrl = window.APP_CONFIG?.configServerUrl || 'https://admin.tranware.net/api/config/driverapp-config';
        
        return axios.get(configServerUrl + '/' + fleetCode.toUpperCase(), req)
            .then((res) => {
                // Store as last known good config
                localStorage.setItem('fleetSettings', JSON.stringify(res.data));
                localStorage.setItem('fleetSettingsTimestamp', Date.now().toString());
                console.log('Fleet settings refreshed from server');
                return res.data;
            })
            .catch((err) => {
                console.error('Settings retrieval failed:', err);
                
                // Fall back to last known good config
                const cachedSettings = localStorage.getItem('fleetSettings');
                if (cachedSettings) {
                    console.warn('Using last known good fleet settings due to fetch error');
                    return JSON.parse(cachedSettings);
                }
                
                throw err;
            });
    },
    getFixedRoute: (fixedRouteId) => {
      const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        return axios.get(`${fleetSettings.fleetUrl}/API/getFixedRoute`, { params: { id: fixedRouteId } })
            .then((res) => {
                console.log('Fixed route data fetched:', res.data);
                return res.data;
            })
            .catch((err) => {
                console.error('Fixed route data fetch failed:', err);
                throw err;
            });
    },
    
    // New enhanced route details endpoint that returns expanded routeDetails object
    getRouteDetails: (routeId, scheduleId = null, runId = null, vehicleId = null) => {
        const params = { id: routeId };
        if (scheduleId) params.scheduleId = scheduleId;
        if (runId) params.runId = runId;
        if (vehicleId) params.vehicleId = vehicleId;
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        
        return axios.get(`${fleetSettings.fleetUrl}/API/Driver/RouteDetails`, { params })
            .then((res) => {
                console.log('Enhanced route details fetched:', res.data);
                // Return the data portion of the response (which contains the complete route data)
                return res.data.success ? res.data.data : res.data;
            })
            .catch((err) => {
                console.error('Enhanced route details fetch failed:', err);
                throw err;
            });
    },
    getTripAssets: function (vin) {
        if (!vin || vin === 'undefined') {
            vin = '';
        }
        const user = JSON.parse(localStorage.getItem('userSettings'));
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        return fetch(fleetSettings.fleetUrl + "/API/Driver/TripAsset?paxId=" + user.Id + "&vin=" + vin || '')
            .then((response) => response.json())
            .then((data) => {
                console.log(data)
                return (data)
            })
            .catch((error) => {
                console.error('Error fetching trips:', error);
                throw error;
            });

    },
    connectSocket: (url) => {
        return new Promise((resolve, reject) => {
          const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
            try {
                let settled = false;
                const settle = (fn) => {
                    if (settled) return;
                    settled = true;
                    fn();
                };

                // Check if browser is offline - don't even try to connect
                const isOffline = !navigator.onLine;
                
                // Socket.IO with reconnection options
                socketConnection = io(url || fleetSettings.fleetUrl, {
                    reconnection: true,
                    reconnectionAttempts: Infinity, // Keep trying indefinitely for offline-first
                    reconnectionDelay: isOffline ? 5000 : 1000, // Slower retries when offline
                    reconnectionDelayMax: 30000,
                    timeout: 10000,
                    autoConnect: !isOffline // Don't auto-connect if offline
                });
                
                socketConnection.io.on('error', (error) => {
                    if (isBenignSocketTransportError(error)) {
                        return;
                    }
                    if (!navigator.onLine) {
                        return;
                    }
                    console.error('Socket.io transport error:', error);
                });
                
                // Initialize connection manager with socket
                connectionManager.setSocket(socketConnection);
                
                socketConnection.on('connect', () => {
                    console.log('Socket connected successfully');
                    notifyConnectionStatus('connected');
                    
                    // Rejoin rooms after connection/reconnection
                    comms.joinDriverAppRoom();
                    
                    settle(() => resolve(socketConnection));
                });
                
                socketConnection.on('disconnect', (reason) => {
                    console.warn('Socket disconnected:', reason);
                    notifyConnectionStatus('disconnected');
                });
                
                socketConnection.on('reconnecting', (attemptNumber) => {
                    console.log('Socket reconnecting, attempt:', attemptNumber);
                    notifyConnectionStatus('reconnecting');
                });
                
                socketConnection.on('reconnect', (attemptNumber) => {
                    console.log('Socket reconnected after', attemptNumber, 'attempts');
                    notifyConnectionStatus('connected');
                    
                    // Rejoin rooms after reconnection
                    comms.joinDriverAppRoom();
                });
                
                socketConnection.on('reconnect_error', (error) => {
                    if (isBenignSocketTransportError(error)) {
                        notifyConnectionStatus('error');
                        return;
                    }
                    console.error('Socket reconnection error:', error);
                    notifyConnectionStatus('error');
                });
                
                socketConnection.on('reconnect_failed', () => {
                    console.error('Socket reconnection failed after max attempts');
                    notifyConnectionStatus('failed');
                });
                
                socketConnection.on('connect_error', (error) => {
                    if (!navigator.onLine) {
                        console.log('Socket connect error while offline - expected');
                        notifyConnectionStatus('offline');
                        return;
                    }
                    if (isBenignSocketTransportError(error)) {
                        console.warn('Socket connect error (retrying):', error.message || error);
                    } else {
                        console.error('Socket connection error:', error);
                    }
                    notifyConnectionStatus('error');
                });
                
                // Handle server-authoritative route updates (server-wins)
                socketConnection.on('routeUpdate', (data) => {
                    console.log('Received authoritative route update from server');
                    connectionManager.handleServerData(data);
                });
                
                // Set a timeout for initial connection
                // When offline, resolve with the socket anyway (will connect when online)
                const timeoutMs = navigator.onLine ? 10000 : 2000; // 2 seconds when offline, 10 when online
                setTimeout(() => {
                    if (settled) return;
                    if (socketConnection.connected) return;
                    console.warn(
                        'Socket not connected after initial wait — continuing; client will keep reconnecting'
                    );
                    notifyConnectionStatus(!navigator.onLine ? 'offline' : 'disconnected');
                    settle(() => resolve(socketConnection));
                }, timeoutMs);
                
            } catch (error) {
                console.error('Error creating socket:', error);
                reject(error);
            }
        });
    },
    
    // Join the driver app room (called on connect and reconnect)
    joinDriverAppRoom: () => {
        const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings'));
        const userSettings = JSON.parse(localStorage.getItem('userSettings'));
        
        // UnitID may be stored under different casings depending on data source:
        // REST auth driverObject: unitId (camelCase), socket formatCompleteVehicleObject: UnitID (PascalCase)
        // dispatchData.vehicleObject: unitID (mixed case from SQL alias)
        const unitID = userSettings?.UnitID || userSettings?.unitID || userSettings?.unitId 
            || userSettings?.dispatchData?.vehicleObject?.unitID 
            || userSettings?.dispatchData?.vehicleObject?.UnitID
            || userSettings?.dispatchData?.vehicleObject?.unitId;
        
        if (socketConnection && fleetSettings && userSettings && unitID) {
            console.log('Joining driver app room for unit:', unitID);
            socketConnection.emit('joinDriverApp', { UnitID: unitID });
        }
        
        // Also rejoin route room if we have an active route
        if (socketConnection && currentRouteId) {
            console.log('Rejoining route room for route:', currentRouteId);
            socketConnection.emit('joinRoute', { routeId: currentRouteId });
        }
    },
    
    // Set active route for automatic room management
    setActiveRoute: (routeId) => {
        currentRouteId = routeId;
        if (socketConnection && routeId) {
            console.log('Setting active route and joining room:', routeId);
            socketConnection.emit('joinRoute', { routeId: routeId });
        }
    },
    
    // Get current active route ID
    getActiveRoute: () => {
        return currentRouteId;
    },
    
    // Persist route data to localStorage for session recovery and offline mode
    persistRouteData: (routeData) => {
        if (!routeData) return;
        
        try {
            // Use offlineStorage service for unified storage
            offlineStorage.saveRouteData(routeData);
            
            // Also maintain legacy format for backward compatibility
            const persistData = {
                routeData: routeData,
                persistedAt: Date.now(),
                activeDate: routeData.activeDate || new Date().toISOString().split('T')[0]
            };
            localStorage.setItem(ROUTE_DATA_KEY, JSON.stringify(persistData));
            localStorage.setItem(ROUTE_PERSIST_TIMESTAMP_KEY, persistData.persistedAt.toString());
            
            // Also update active route ID for room management
            if (routeData.routeId) {
                currentRouteId = routeData.routeId;
            }
            
            console.log('Route data persisted to localStorage');
        } catch (err) {
            console.error('Failed to persist route data:', err);
        }
    },
    
    // Get persisted route data if valid (not stale, same day)
    // First tries offlineStorage, falls back to legacy format
    getPersistedRouteData: () => {
        try {
            // Try offlineStorage first (newer, more robust)
            const offlineData = offlineStorage.getRouteData();
            if (offlineData) {
                // Update active route ID for room management
                if (offlineData.routeId) {
                    currentRouteId = offlineData.routeId;
                }
                console.log('Retrieved route data from offlineStorage');
                return offlineData;
            }
            
            // Fall back to legacy format
            const persistedStr = localStorage.getItem(ROUTE_DATA_KEY);
            if (!persistedStr) return null;
            
            const persisted = JSON.parse(persistedStr);
            const now = Date.now();
            const age = now - persisted.persistedAt;
            
            // Check if data is too old
            if (age > MAX_ROUTE_DATA_AGE_MS) {
                console.log('Persisted route data is stale, clearing...');
                comms.clearPersistedRouteData();
                return null;
            }
            
            // Check if activeDate matches today
            const today = new Date().toISOString().split('T')[0];
            const persistedDate = persisted.activeDate?.split('T')[0];
            
            if (persistedDate && persistedDate !== today) {
                console.log('Persisted route data is from different day, clearing...');
                comms.clearPersistedRouteData();
                return null;
            }
            
            // Update active route ID
            if (persisted.routeData?.routeId) {
                currentRouteId = persisted.routeData.routeId;
            }
            
            console.log('Retrieved valid persisted route data (legacy)');
            return persisted.routeData;
        } catch (err) {
            console.error('Failed to get persisted route data:', err);
            return null;
        }
    },
    
    // Clear persisted route data (both new and legacy)
    clearPersistedRouteData: () => {
        localStorage.removeItem(ROUTE_DATA_KEY);
        localStorage.removeItem(ROUTE_PERSIST_TIMESTAMP_KEY);
        offlineStorage.clearRouteData();
        currentRouteId = null;
        console.log('Persisted route data cleared');
    },
    
    // Subscribe to connection status changes
    // Returns unsubscribe function
    onConnectionStatusChange: (callback) => {
        connectionStatusCallbacks.push(callback);
        // Immediately call with current status
        callback(connectionStatus, null);
        return () => {
            connectionStatusCallbacks = connectionStatusCallbacks.filter(cb => cb !== callback);
        };
    },
    
    // Get current connection status
    getConnectionStatus: () => {
        return connectionStatus;
    },
    
    // Manually trigger reconnection attempt
    triggerReconnect: () => {
        if (socketConnection) {
            console.log('Manually triggering socket reconnection...');
            notifyConnectionStatus('reconnecting');
            socketConnection.connect();
        } else {
            console.warn('No socket connection to reconnect');
            // Try to create a new connection
            const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings'));
            if (fleetSettings && fleetSettings.fleetUrl) {
                void comms.connectSocket(fleetSettings.fleetUrl).catch(() => {});
            }
        }
    },
    
    postTowing: function (data) {
     
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        const req = data;
        console.log("comms.postTowing req:", req);

        return axios.post(`${fleetSettings.fleetUrl}/API/Driver/Towing`, req, {
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then((res) => {
                console.log("comms.postTowing res:", res.data);
                if (!res.data.error) {
                    return res.data;
                } else {
                    throw new Error(res.data.error);
                }
            })
            .catch((error) => {
                console.error('Error posting towing data:', error.message || error);
                throw error;
            });
    },
    postTowingComplete: function (data) {
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))

        const req = data;
        console.log("comms.postTowing req:", req);

        return axios.post(`${fleetSettings.fleetUrl}/API/Driver/TowingComplete`, req, {
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then((res) => {
                console.log("comms.postTowing res:", res.data);
                if (!res.data.error) {
                    return res.data;
                } else {
                    throw new Error(res.data.error);
                }
            })
            .catch((error) => {
                console.error('Error posting towing data:', error.message || error);
                throw error;
            });
    },
    getInspectionItems: async () => {
        try {
          const user = JSON.parse(localStorage.getItem('userSettings')) || {};
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
          // VehicleID is the VEHICLE's Id (not the driver's Id)
          // May be stored as VehicleID (explicit alias), or found in dispatchData.vehicleObject
          const vehicleId = user.VehicleID || user.dispatchData?.vehicleObject?.id || user.dispatchData?.vehicleObject?.Id || user.dispatchData?.vehicleId;
          const req = {
            driverId: user.Id || user.id,
            vehicleId: vehicleId,
          };
    
          console.log("comms.getInspectionItems req:", req);
    
          const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Inspection`, {
            params: { id: user.Id },
            headers: {
              'Content-Type': 'application/json',
            },
          });
    
          console.log("comms.getInspectionItems res:", response.data);
          if (!response.data.error) {
            return response.data;
          } else {
            throw new Error(response.data.error);
          }
    
        } catch (error) {
          console.error("comms.getInspectionItems error:", error);
          throw error;
        }
    },
    postInspectionItems: async (data) => {
      try {
          const user = JSON.parse(localStorage.getItem('userSettings')) || {};
                const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))

          // VehicleID is the VEHICLE's Id (not the driver's Id)
          const vehicleId = user.VehicleID || user.dispatchData?.vehicleObject?.id || user.dispatchData?.vehicleObject?.Id || user.dispatchData?.vehicleId;
          const req = {
          ...data,
          driverId: user.Id || user.id,
          vehicleId: vehicleId,
          };

          console.log("comms.postInspectionItems req:", req);

          const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Inspection`, req, {
          headers: {
              'Content-Type': 'application/json',
          },
          });

          console.log("comms.postInspectionItems res:", response.data);
          if (!response.data.error) {
          return response.data;
          } else {
          throw new Error(response.data.error);
          }

      } catch (error) {
          console.error("comms.postInspectionItems error:", error);
          throw error;
      }
    },
      /**
       * Send a message through the socket connection
       * If offline, queues the message for later sync with original timestamp
       * 
       * @param {string} message - Message to send
       * @param {Object} options - Options for offline handling
       * @param {Function} options.onOptimisticUpdate - Callback for optimistic UI update
       * @param {boolean} options.skipOfflineQueue - If true, don't queue when offline (for LiveDispatch)
       * @param {number} [options.ackTimeoutMs] - Max wait for Socket.IO emit ack (default 5000). Server often omits ack.
       * @returns {Promise} - Resolves when sent or queued
       */
    sendSocket: function(message, options = {}) {
        const userSettings=JSON.parse(localStorage.getItem('userSettings'))
   
        let driverNumber=localStorage.getItem('driverNumber')
        try{
            driverNumber=JSON.parse(localStorage.getItem('driverNumber'))
        }catch(e){}

        
        if (!driverNumber || !outGoingMessageNumber) {
            console.error('Driver number not found');
            return Promise.reject(new Error('Driver number not found'));
        }

        const messageFormat = (userSettings.unitId || driverNumber) + ":" + outGoingMessageNumber + "|" + message;
        const timestamp = Date.now(); // Capture when action occurred
        outGoingMessageNumber++;
        
        // Apply optimistic update immediately if provided
        if (options.onOptimisticUpdate) {
            options.onOptimisticUpdate();
        }

        // If socket connected, try to send immediately.
        // Many Tranware handlers do not call the Socket.IO ack callback; without a timeout the
        // Promise would never resolve (e.g. Flag Trip modal stuck on "Submitting...").
        if (socketConnection && socketConnection.connected) {
            const ackMs =
                typeof options.ackTimeoutMs === 'number' && options.ackTimeoutMs > 0
                    ? options.ackTimeoutMs
                    : 5000;
            return new Promise((resolve) => {
                let settled = false;
                const finish = (payload) => {
                    if (settled) return;
                    settled = true;
                    resolve(payload);
                };
                const timer = setTimeout(() => {
                    // The Tranware protocol does NOT use Socket.IO acks for most messages —
                    // the server's response (e.g. STARTM for START, JOBMF for FLAGM) IS
                    // the ack, and outgoing ACK|nn messages are themselves acks (never
                    // ack'd back). Only emit a noisy warning when the caller explicitly
                    // opted in via options.expectAck. Otherwise log at debug level so the
                    // console isn't drowned in benign timeout messages.
                    if (options.expectAck) {
                        console.warn(
                            'sendSocket: no server ack within',
                            ackMs,
                            'ms — treating as sent. Message:',
                            String(message).slice(0, 80)
                        );
                    } else {
                        // eslint-disable-next-line no-console
                        console.debug(
                            'sendSocket: ack timeout (expected for protocol). Message:',
                            String(message).slice(0, 80)
                        );
                    }
                    finish({ sent: true, ackTimeout: true });
                }, ackMs);
                socketConnection.emit('Message', messageFormat, (response) => {
                    clearTimeout(timer);
                    console.log('Message sent:', messageFormat);
                    finish({ sent: true, response });
                });
            });
        }
        
        // For LiveDispatch mode (skipOfflineQueue=true), don't queue - just fail silently
        // LiveDispatch requires active connection and messages like ACPTM expire quickly
        if (options.skipOfflineQueue) {
            console.log('Offline - skipping queue for LiveDispatch message:', message);
            return Promise.resolve({ sent: false, queued: false, reason: 'offline_not_queued' });
        }
        
        // #8 Offline HTTP fallback: try delivering the pipe-delimited message via
        // POST /API/Driver/Dispatch before queueing, so driver→dispatch commands
        // still reach the server's inbound queue when the socket is down. Only on
        // failure do we fall back to the durable offline queue (flushed on reconnect).
        console.log('Offline - attempting HTTP dispatch fallback:', messageFormat);
        return comms.sendDispatchMessage(messageFormat)
            .then((res) => {
                if (res && (res.status === true || res.success === true)) {
                    console.log('Offline - delivered via HTTP fallback:', messageFormat);
                    return { sent: true, viaHttp: true };
                }
                throw new Error('HTTP dispatch fallback did not confirm delivery');
            })
            .catch((err) => {
                console.log('Offline - HTTP fallback failed, queueing message:', messageFormat, err?.message);
                const actionId = offlineStorage.queueAction({
                    type: 'Message',
                    data: { message, driverNumber, unitId: userSettings?.unitId },
                    messageFormat: messageFormat,
                    timestamp: timestamp, // When the action actually occurred
                });
                return { sent: false, queued: true, actionId };
            });
    },

    assignVehicleToRoute: async function(routeId, vehicleId, scheduleId, scheduleName, sourceType='itinerary', driverId=null) {
              const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))

        const url = `${fleetSettings.fleetUrl}/API/assignVehicleToRoute`;
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ routeId: routeId, vehicleId, scheduleId, scheduleName, sourceType, driverId }),
          });
          const data = await response.json();
          return data;
        } catch (error) {
          console.error("Error assigning vehicle to route:", error);
          throw error;
        }
      },


      /**
       * Get list of all fixed routes
       * @returns {Promise} - Promise resolving to list of routes
       */
      getFixedRouteList: async function() {
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        const url = `${fleetSettings.fleetUrl}/api/fixedRouteList`;
        try {
          const response = await fetch(url);
          const data = await response.json();
          return data;
        } catch (error) {
          console.error("Error fetching fixed route list:", error);
          throw error;
        }
      },
      
      /**
       * Save passenger data for a fixed route stop
       * @param {Object} data - Passenger data
       * @returns {Promise} - Promise resolving to response
       */
      saveFixedRoutePassengerData: async function(data) {
        const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        const url = `${fleetSettings.fleetUrl}/api/fixedRoutePassengerData`;
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });
          return await response.json();
        } catch (error) {
          console.error("Error saving passenger data:", error);
          throw error;
        }
      },
      
      /**
       * Get passenger data for a fixed route
       * @param {string} routeId - ID of route
       * @param {string} date - Date in YYYY-MM-DD format (optional)
       * @returns {Promise} - Promise resolving to passenger data
       */
      getFixedRoutePassengerData: async function(routeId, date) {
                const fleetSettings=JSON.parse(localStorage.getItem('fleetSettings'))
        const dateParam = date ? `&date=${date}` : '';
        const url = `${fleetSettings.fleetUrl}/api/fixedRoutePassengerData?routeId=${routeId}${dateParam}`;
        try {
          const response = await fetch(url);
          const data = await response.json();
          return data;
        } catch (error) {
          console.error("Error fetching passenger data:", error);
          throw error;
        }
      },
    
      // Driver Status Functions (LiveDispatch - require active connection, no offline queue)
      logon: (driverNumber, pin) => {
        const userSettings = JSON.parse(localStorage.getItem('userSettings'));
        if (!socketConnection || !userSettings) {
            console.error('Socket connection or user settings not available');
            return;
        }
        comms.sendSocket(`LOGON|${pin.toString().padStart(4, "0")}`, { skipOfflineQueue: true });
    },
    
    logoff: () => {
        const userSettings = JSON.parse(localStorage.getItem('userSettings'));
        if (!socketConnection || !userSettings) {
            console.error('Socket connection or user settings not available');
            return;
        }
        comms.sendSocket('LOGOF', { skipOfflineQueue: true });
    },
    
    setBusy: () => {
        comms.sendSocket('BUSY', { skipOfflineQueue: true });
    },
    
    setClear: () => {
        comms.sendSocket('CLEAR', { skipOfflineQueue: true });
    },
    
    // Trip Status Functions (LiveDispatch - require active connection, no offline queue)
    acceptTrip: (tripNumber) => {
        comms.sendSocket(`ACPTM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    rejectTrip: (tripNumber) => {
        comms.sendSocket(`REJCM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setEnroute: (tripNumber) => {
        comms.sendSocket(`ENRTE|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setArrivedOrigin: (tripNumber) => {
        comms.sendSocket(`ORIG|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setLoaded: (tripNumber) => {
        comms.sendSocket(`LOADM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setArrivedDestination: (tripNumber) => {
        comms.sendSocket(`DEST|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setNoShow: (tripNumber) => {
        comms.sendSocket(`NOSHM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    setCompleted: (tripNumber) => {
        comms.sendSocket(`COMPM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    /**
     * Send PRICE message with pricing items array before completion.
     *
     * Wire format: `PRICE|<tripNumber>|<JSON [items]>`. The server's PRICE handler
     * (lib/dispatch.js jobUpdatePriceItems) does `async.eachSeries(JSON.parse(payload), …)`
     * which only works on arrays — passing an object like `{items, disposition}` causes
     * eachSeries to iterate the object's values (the items array itself, then the
     * disposition object) and crash on `priceItem.name.indexOf("Wait")` because neither
     * value has a string `name`. Disposition fields (tip/toll/surcharge) are accepted as
     * a parameter for forward compatibility but are NOT sent on the wire today; if/when
     * the server grows a disposition channel, route them through a separate message.
     */
    sendPrice: (tripNumber, priceItems, _dispositionUpdates = null) => {
        const items = Array.isArray(priceItems) ? priceItems : [];
        const json = JSON.stringify(items);
        comms.sendSocket(`PRICE|${tripNumber}|${json}`, { skipOfflineQueue: true });
    },

    // PRICE-before-COMPM: send PRICE, wait for server PRICE|Ok (see setupSocketHandlers), then COMPM
    completeWithPricing: (tripNumber, priceItems, dispositionUpdates = null) => {
        const trip = String(tripNumber ?? '').trim();
        if (!trip) {
            console.error('completeWithPricing: missing trip number');
            return;
        }
        if (pendingPriceComplete) {
            console.warn(
                'completeWithPricing: clearing prior pending ack for trip',
                pendingPriceComplete.tripNumber
            );
            clearPendingPriceComplete();
        }
        pendingPriceComplete = {
            tripNumber: trip,
            timeoutId: setTimeout(() => {
                if (!pendingPriceComplete || String(pendingPriceComplete.tripNumber).trim() !== trip) {
                    return;
                }
                console.warn('PRICE server ack timeout; sending COMPM anyway for trip', trip);
                clearPendingPriceComplete();
                comms.sendSocket(`COMPM|${trip}`, { skipOfflineQueue: true });
            }, PRICE_ACK_TIMEOUT_MS),
        };
        comms.sendPrice(trip, priceItems, dispositionUpdates);
    },
    
    triggerCallout: (tripNumber) => {
        comms.sendSocket(`CALLM|${tripNumber}`, { skipOfflineQueue: true });
    },
    
    updatePayment: (tripNumber, paymentType, amount) => {
        // paymentType: CREDIT, CASH, or ACCOUNT
        comms.sendSocket(`EPMTM|${tripNumber}|${paymentType}|${amount || ''}`, { skipOfflineQueue: true });
    },
    
    // Communication Functions (LiveDispatch chat - require active connection)
    sendMessage: (message) => {
        comms.sendSocket(`MSG|${message}`, { skipOfflineQueue: true });
    },

    // ── Emergency / panic alarm ──────────────────────────────────────────────
    // Sends a high-priority distress alert to dispatch. The Tranware protocol has
    // no dedicated emergency command, so for guaranteed visibility we deliver
    // through the existing MSG| dispatch channel (the operator already receives
    // and displays these). Delivery is DURABLE (no skipOfflineQueue): if the
    // socket is down it falls back to HTTP and the offline queue so a panic alert
    // is never silently dropped. A best-effort structured EMERGENCY| command is
    // also emitted in case the backend ever routes it (ignored otherwise).
    sendEmergencyAlert: async (payload = {}) => {
        const { latitude, longitude } = payload;
        const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);
        let userSettings = {};
        try { userSettings = JSON.parse(localStorage.getItem('userSettings')) || {}; } catch (e) {}
        const driverName = userSettings.Name || userSettings.name || '';
        const who = driverName ? ` (${driverName})` : '';
        const loc = hasCoords ? `${latitude.toFixed(6)},${longitude.toFixed(6)}` : 'location unavailable';
        const mapLink = hasCoords ? ` https://maps.google.com/?q=${latitude},${longitude}` : '';
        const text = `*** EMERGENCY ALARM *** driver requesting immediate assistance${who} @ ${loc}${mapLink}`;
        // Best-effort structured command — do not block the guaranteed path on it.
        try {
            comms.sendSocket(`EMERGENCY|${JSON.stringify({ ...payload, name: driverName, ts: Date.now() })}`, {
                skipOfflineQueue: true,
                ackTimeoutMs: 4000,
            });
        } catch (e) { /* ignore — MSG path below is the guaranteed delivery */ }
        // Guaranteed, durable dispatch alert (queues/HTTP-falls-back if offline).
        return comms.sendSocket(`MSG|${text}`, { ackTimeoutMs: 4000 });
    },

    // Driver "stand down" — tells dispatch the emergency is resolved.
    cancelEmergencyAlert: async (payload = {}) => {
        let userSettings = {};
        try { userSettings = JSON.parse(localStorage.getItem('userSettings')) || {}; } catch (e) {}
        const driverName = userSettings.Name || userSettings.name || '';
        const who = driverName ? ` (${driverName})` : '';
        return comms.sendSocket(`MSG|EMERGENCY CANCELLED — driver stood down${who}`, { ackTimeoutMs: 4000 });
    },
    
    // Convert a GeolocationPosition speed (meters/second) to whole MPH.
    // Returns 0 when speed is unavailable/invalid (matches server default).
    mpsToMph: (speedMs) =>
        Number.isFinite(speedMs) && speedMs >= 0 ? Math.round(speedMs * 2.23694) : 0,

    // GPS via legacy message format (LiveDispatch - skip offline queue)
    sendGPS: (latitude, longitude, speed = 0) => {
        const latDir = latitude >= 0 ? 'N' : 'S';
        const lonDir = longitude >= 0 ? 'E' : 'W';
        const gpsString = `${latDir} ${Math.abs(latitude)},${lonDir} ${Math.abs(longitude)},${speed}`;
        comms.sendSocket(`GPS|${gpsString}`, { skipOfflineQueue: true });
    },

    /** Register handler for MSG while Flag Trip waits for reverse-geocode / create errors (cleared on modal close). */
    setFlagTripAwaitingMessageHandler: (fn) => {
        flagTripAwaitingMessageHandler = typeof fn === 'function' ? fn : null;
    },

    /** LiveDispatch: record unscheduled flag trip (server routes FLAGM → createFlagTrip) */
    sendFlagTrip: function (location, passengerCount) {
        const flagData = {
            location: { latitude: location.lat, longitude: location.lng },
        };
        const n = parseInt(passengerCount, 10);
        if (!Number.isNaN(n) && n >= 1) {
            flagData.passengerCount = n;
        }
        return comms.sendSocket(`FLAGM|${JSON.stringify(flagData)}`, {
            skipOfflineQueue: true,
            ackTimeoutMs: 4000,
        });
    },

    /**
     * Send GPS update via socket (fire-and-forget)
     * GPS updates are NOT queued for offline sync since server doesn't ACK them.
     * If offline, GPS updates are simply not sent - they're not critical.
     */
    sendGPSUpdate: (latitude, longitude, speed, vehicleId, routeId, routeType, routeGroupId, vehicleObject = null) => {
        // Only send if socket is connected - GPS updates are fire-and-forget
        if (!socketConnection || !socketConnection.connected) {
            // Silently skip - GPS updates are not critical for offline sync
            return;
        }
        
        const timestamp = Date.now();
        // Read fleetSettings fresh — the module-level copy is captured once at import
        // and is null on a fresh session, which would send unitId: undefined.
        const currentFleetSettings = JSON.parse(localStorage.getItem('fleetSettings') || '{}');
        const gpsUpdateData = {
            latitude: latitude,
            longitude: longitude,
            speed: speed,
            vehicleId: vehicleId,
            vehicleObject: vehicleObject,
            routeId: routeId,
            routeType: routeType,
            routeGroupId: routeGroupId,
            timestamp: new Date(timestamp).toISOString(),
            originalTimestamp: timestamp,
            unitId: currentFleetSettings?.unitId
        };
        
        // Fire-and-forget - no callback, no queueing
        socketConnection.emit('GPS_UPDATE', gpsUpdateData);
    },

    joinRouteGroup: (unitId, vehicleId, routeId, routeType, routeGroupId) => {
        const groupData = {
            unitId: unitId,
            vehicleId: vehicleId,
            routeId: routeId,
            routeType: routeType,
            routeGroupId: routeGroupId
        };
        
        if (socketConnection) {
            socketConnection.emit('joinRouteGroup', groupData);
        }
    },

    leaveRouteGroup: (vehicleId, routeId, routeGroupId) => {
        if (socketConnection) {
            socketConnection.emit('leaveRouteGroup', {
                vehicleId: vehicleId,
                routeId: routeId,
                routeGroupId: routeGroupId
            });
        }
    },
    
    // Socket Message Handlers
    setupSocketHandlers: (socket, callbacks = {}) => {
        if (!socket) return;
        
        // Handle incoming commands from server
        socket.on('Command', (command) => {
             const parts = command.split('|').slice(1); // Skip the first part which is the message ID
            const commandType = parts[0];
            
            switch (commandType) {
                case 'START':
                    let status = parts[1];
                    if (status === ' ') {
                        if (callbacks.onStartNoJobs) {
                            callbacks.onStartNoJobs();
                        }
                    }
                    else if (status === 'O') {
                        callbacks.onStartWithJobs && callbacks.onStart("OffService");
                    }
                    else if (status === 'L'){
                        callbacks.onStart && callbacks.onStart(parts[1]);
                    }
                    break;
                case 'STARTM':
                case 'STARTN':
                    // STARTN: same jobs-array payload as STARTM on some dispatch builds
                    callbacks.onStartM && callbacks.onStartM(parts[1]);
                    break;
                case 'LOGON':
                    callbacks.onLogon && callbacks.onLogon(parts[1]);
                    break;
                case 'LOGOF':
                    callbacks.onLogoff && callbacks.onLogoff(parts[1]);
                    break;
                case 'BUSY':
                    callbacks.onBusy && callbacks.onBusy(parts[1]);
                    break;
                case 'CLEAR':
                    callbacks.onClear && callbacks.onClear(parts[1]);
                    break;
                case 'PING':
                    callbacks.onPing && callbacks.onPing();
                    break;
                case 'MSG': {
                    const msgText = parts[1];
                    if (flagTripAwaitingMessageHandler) {
                        try {
                            flagTripAwaitingMessageHandler(msgText);
                        } catch (err) {
                            console.error('flagTripAwaitingMessageHandler error:', err);
                        }
                    }
                    callbacks.onMessage && callbacks.onMessage(msgText);
                    break;
                }
                case 'MSGN':
                    callbacks.onMessageBulk && callbacks.onMessageBulk(parts[1]);
                    break;
                case 'MSGA':
                    callbacks.onMessageReceived && callbacks.onMessageReceived(parts[1]);
                    break;
                case 'JOBOM':
                    callbacks.onJobOffer && callbacks.onJobOffer(parts[1], parts[2]);
                    break;
                case 'JOBM':
                    // parts[1] = trip number, parts[2..n] = job JSON (rejoin in case JSON contains pipes)
                    callbacks.onJobDetails && callbacks.onJobDetails(parts[1], parts.slice(2).join('|'));
                    break;
                case 'JOBMF':
                    // Same rejoin as JOBM — job JSON can contain pipe characters.
                    callbacks.onFlagTrip && callbacks.onFlagTrip(parts[1], parts.slice(2).join('|'));
                    break;
                case 'DELJM':
                    callbacks.onDeleteJob && callbacks.onDeleteJob(parts[1]);
                    break;
                case 'PMNTM':
                    callbacks.onSetInAppPayment && callbacks.onSetInAppPayment(parts[1]);
                    break;
                case 'LOADM':
                    callbacks.onSetLoaded && callbacks.onSetLoaded(parts[1]);
                    break;
                case 'COMPM':
                    callbacks.onSetCompleted && callbacks.onSetCompleted(parts[1]);
                    break;
                case 'PRICE': {
                    const t1 = parts[1] != null ? String(parts[1]).trim() : '';
                    const t2 = parts[2] != null ? String(parts[2]).trim() : '';
                    const isOk = (s) => String(s).toLowerCase() === 'ok';
                    let ok = false;
                    let tripFromAck = '';
                    if (isOk(t1) && t2) {
                        ok = true;
                        tripFromAck = t2;
                    } else if (t1 && isOk(t2)) {
                        ok = true;
                        tripFromAck = t1;
                    } else if (isOk(t1) && !t2) {
                        ok = true;
                        tripFromAck = '';
                    }
                    if (ok) {
                        const tripHint =
                            tripFromAck ||
                            (pendingPriceComplete ? String(pendingPriceComplete.tripNumber).trim() : '');
                        callbacks.onPriceOk && callbacks.onPriceOk(tripHint, true);
                        tryCompleteAfterPriceAck(tripFromAck);
                    } else {
                        callbacks.onPriceOk && callbacks.onPriceOk(t1, false);
                    }
                    break;
                }
                case 'EPMTM':
                    callbacks.onRiderAppPayDriver && callbacks.onRiderAppPayDriver(parts[1], parts[2]);
                    break;
                case 'DRIVER':
                    callbacks.onSetDriver && callbacks.onSetDriver(JSON.parse(parts[1]));
                    break;
                case 'BOOK':
                    callbacks.onBookInZone && callbacks.onBookInZone(parts[1]);
                    break;
                case 'SOONM':
                    callbacks.onSoon && callbacks.onSoon(parts[1]);
                    break;
                case 'ZONEA':
                    callbacks.onZoneUpdate && callbacks.onZoneUpdate(parts[1]);
                    break;
                default:
                    console.log('Unknown command type:', commandType);
            }
        });
    },
    
    // Utility Functions
    getCurrentTripStatus: () => {
        return JSON.parse(localStorage.getItem('currentTripStatus')) || null;
    },
    
    setCurrentTripStatus: (status) => {
        localStorage.setItem('currentTripStatus', JSON.stringify(status));
    },
    
    getCurrentVehicleStatus: () => {
        return localStorage.getItem('currentVehicleStatus') || 'OffService';
    },
    
    setCurrentVehicleStatus: (status) => {
        localStorage.setItem('currentVehicleStatus', status);
    },
    
    // Driver odometer functions (for future use)
    sendStartMileage: (mileage) => {
        comms.sendSocket(`DMILE|${mileage}`);
    },
    
    sendEndMileage: (mileage) => {
        comms.sendSocket(`SMILE|${mileage}`);
    },
    
    sendAcceptMileage: (tripNumber, mileage) => {
        comms.sendSocket(`AMILM|${tripNumber}|${mileage}`);
    },
    
    sendBeginMileage: (tripNumber, mileage) => {
        comms.sendSocket(`BMILM|${tripNumber}|${mileage}`);
    },
    
    // SoftMeter functions — send meter telemetry and final distance to dispatch
    sendMeter: (stats) => {
        const msg =
          `totalDistance: ${Math.round(stats.totalDistance * 100) / 100}` +
          ` totalTime: ${stats.totalTime}` +
          ` distanceFare: ${Math.round(stats.distanceFare * 100) / 100}` +
          ` timeFare: ${stats.timeFare}` +
          ` gpsSpeed: ${stats.gpsSpeed}` +
          ` fareMode: ${stats.fareMode}`;
        comms.sendSocket(`METER|${stats.jobNumber}|${msg}`, { skipOfflineQueue: true });
    },

    sendMeterDistance: (jobNumber, distanceValue) => {
        // Mirrors Java MessageGenerator.distance(): scale 1, round up, remove decimal point
        const rounded = Math.ceil(distanceValue * 10) / 10;  // round up to 1 decimal
        const encoded = rounded.toFixed(1).replace('.', '');
        comms.sendSocket(`MDIST|${jobNumber}|${encoded}`, { skipOfflineQueue: true });
    },
    
    getChatHistory: (vehicleNumber, callback) => {
    if (socketConnection && socketConnection.connected) {
      socketConnection.emit('driverAppMessage', {
        command: 'getChatHistory',  
        vehicleNumber: vehicleNumber
      });
      
      // Listen for the response
      socketConnection.once('chatHistory', callback);
    } else {
      console.error('Socket not connected - cannot get chat history');
      callback({ error: 'Socket not connected' });
    }
  },

  // Get driver schedule (itinerary + trip fallback) for a specific date
  getDriverSchedule: async (driverId, date) => {
    try {
      // Read fleetSettings fresh from localStorage (the module-level copy is captured
      // once at import time and is null/stale on a fresh session — see line ~48).
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) {
        throw new Error('Fleet settings not available (no fleetUrl)');
      }
      // API doc expects date as YYYY-MM-DD. Normalize any YYYY/MM/DD callers.
      const normalizedDate = date ? String(date).replace(/\//g, '-') : date;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Schedule`, {
        driverId: driverId,
        date: normalizedDate // YYYY-MM-DD format
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching driver schedule:', error);
      throw error;
    }
  },

  // ── Driver dashboard feature endpoints ──────────────────────────────
  // All read fleetSettings fresh from localStorage and target the fleet's
  // AutoDispatch host (e.g. https://twezr-nj.qryde.net:8079) via fleetUrl.
  // Endpoints/bodies follow AutoDispatch_API_Documentation.md.

  // POST /API/Driver/History  { driverId }  → past trips
  getDriverHistory: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/History`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver history:', error);
      throw error;
    }
  },

  // POST /API/Driver/Balance  { driverId }  (proxy to SaaS RemoteCashiering)
  getDriverBalance: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Balance`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver balance:', error);
      throw error;
    }
  },

  // POST /API/Driver/Transactions  { driverId }
  getDriverTransactions: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Transactions`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver transactions:', error);
      throw error;
    }
  },

  // POST /API/Driver/Deposits  { driverId }
  getDriverDeposits: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Deposits`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver deposits:', error);
      throw error;
    }
  },

  // POST /API/Driver/Payments  { driverId }
  getDriverPayments: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Payments`, { driverId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver payments:', error);
      throw error;
    }
  },

  // POST /API/Driver/Receipt  { driverId, receiptId }  (receiptId required)
  getDriverReceipt: async (driverId, receiptId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Receipt`, { driverId, receiptId });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver receipt:', error);
      throw error;
    }
  },

  // POST /API/Trips/Receipt  { tripId, phone?, email? }
  // Sends a trip receipt to the rider by SMS and/or email.
  sendReceipt: async (tripId, { phone, email } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { tripId };
      if (phone) body.phone = phone;
      if (email) body.email = email;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Trips/Receipt`, body);
      return response.data;
    } catch (error) {
      console.error('Error sending receipt:', error);
      throw error;
    }
  },

  // POST /API/Driver/Cashier  { driverId, lineItems }
  // Records driver cash activity (e.g. a cash deposit) against the live financial
  // ledger. In mock mode we record the deposit locally so the Cash screen's
  // deposit flow can be tested end-to-end. The LIVE write is still gated: the
  // real Cashier endpoint requires an undocumented lineItem schema (a
  // driverBalanceId GUID + glAccountId + a required decimal) and rejects partial
  // payloads with "Cannot convert null to 'decimal'", so we do not blindly post
  // guessed values to a shared production ledger.
  driverCashier: async (driverId, lineItems) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Cashier`, { driverId, lineItems });
      return response.data;
    } catch (error) {
      console.error('Error posting driver cashier line items:', error);
      throw error;
    }
  },

  // POST /API/Trips/Update  { apiKey, tripId, ...fields }
  // Persists driver edits to a booking. Like trip creation, the fleet requires
  // the fleet apiKey on this write; without it the server rejects the request.
  // In mock mode we echo the change back so the edit UI can be tested end-to-end
  // without touching a live booking.
  updateTrip: async (tripId, changes) => {
    const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
    if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
    const apiKey =
      fleetSettings.apiKey || fleetSettings.ApiKey || fleetSettings.fleetApiKey || fleetSettings.FleetApiKey;
    if (!apiKey) {
      throw new Error('Editing bookings requires a fleet apiKey, which is not configured for this fleet.');
    }
    try {
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Trips/Update`, {
        apiKey,
        tripId,
        ...changes,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating trip:', error);
      throw error;
    }
  },

  // GET /API/Driver/Messages?Id=<driverId>
  getDriverMessages: async (driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Messages`, {
        params: { Id: driverId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching driver messages:', error);
      throw error;
    }
  },

  // Add getSocket method for accessing the socket connection
  getSocket: () => {
    return socketConnection;
  },

  // Join driver app room 
  joinDriverApp: () => {
    const userSettings = JSON.parse(localStorage.getItem('userSettings'));
    // UnitID fallback chain - same as joinDriverAppRoom
    const unitID = userSettings?.UnitID || userSettings?.unitID || userSettings?.unitId 
        || userSettings?.dispatchData?.vehicleObject?.unitID 
        || userSettings?.dispatchData?.vehicleObject?.UnitID
        || userSettings?.dispatchData?.vehicleObject?.unitId;
    if (socketConnection && userSettings && unitID) {
      console.log('Joining driver app room for unit:', unitID);
      socketConnection.emit('joinDriverApp', { UnitID: unitID });
      return true;
    }
    return false;
  },

  // Get available vehicles for vehicle selection
  getAvailableVehicles: async () => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Vehicles`);
        // alphabatize the vehicle list by Name
        response.data.sort((a, b) => a.Name.localeCompare(b.Name));
        // Names that are just numbers at the bottom of the list
        response.data.sort((a, b) => {
          const aIsNumeric = /^\d+$/.test(a.Name);
          const bIsNumeric = /^\d+$/.test(b.Name);
            if (aIsNumeric && !bIsNumeric) return 1;
            if (!aIsNumeric && bIsNumeric) return -1;
            return 0;
        });

      return response.data;
    } catch (error) {
      console.error('Error fetching available vehicles:', error);
      throw error;
    }
  },

  // Get available routes for route selection
  getAvailableRoutes: async (vehicleTypeId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/Routes`, {
        params: { vehicleTypeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching available routes:', error);
      throw error;
    }
  },

  // Call shift close
  closeShift: async (driverId, vehicleId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/ShiftClose`, {
        driverId,
        vehicleId
      });
      return response.data;
    } catch (error) {
      console.error('Error closing shift:', error);
      throw error;
    }
  },

  // ── Feature spec endpoints (driver.js authoritative) ────────────────

  // #1 Resume a shift the driver closed earlier today (route still scheduled).
  // Reuses the existing assignment path (POST /API/assignVehicleToRoute).
  resumeShift: async (routeId, vehicleId, driverId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/assignVehicleToRoute`, {
        routeId,
        vehicleId,
        driverId,
        sourceType: 'itinerary',
      });
      return response.data;
    } catch (error) {
      console.error('Error resuming shift:', error);
      throw error;
    }
  },

  // #2 Driver sticker data (proxies SaaS RemoteCashiering function:'sticker').
  // POST /API/Driver/Sticker { driverId, testDate? }
  getDriverSticker: async (driverId, testDate) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = testDate ? { driverId, testDate } : { driverId };
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Sticker`, body);
      return response.data;
    } catch (error) {
      console.error('Error fetching driver sticker:', error);
      throw error;
    }
  },

  // #2 Server-side printable sticker HTML from the sticker/insurance object.
  // POST /API/Driver/Sticker/Build (returns HTML string). Optional — UI can
  // fall back to client-side rendering + window.print() if this 404s.
  buildSticker: async (stickerData) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Sticker/Build`, stickerData);
      return response.data;
    } catch (error) {
      console.error('Error building sticker HTML:', error);
      throw error;
    }
  },

  // #3 List claimable (unclaimed future) routes — route bidding.
  // GET /API/Driver/ClaimableRoutes  params: serviceDate | fromDate&toDate, vehicleTypeId
  getClaimableRoutes: async (params = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.get(`${fleetSettings.fleetUrl}/API/Driver/ClaimableRoutes`, { params });
      return response.data;
    } catch (error) {
      // Route bidding is gated server-side (appVar.routeBidding.enabled, default
      // off) and may not be deployed on this fleet at all — that returns 404.
      // Treat it as "feature unavailable" and degrade gracefully instead of
      // surfacing a raw "Request failed with status code 404" to the driver.
      if (error?.response?.status === 404) {
        return { status: false, available: false, routes: [] };
      }
      console.error('Error fetching claimable routes:', error);
      throw error;
    }
  },

  // #4 Claim a route first-come-first-served (optionally extend shift end).
  // POST /API/Driver/ClaimRoute { routeId, driverId, vehicleId?, endDateTime? }
  claimRoute: async ({ routeId, driverId, vehicleId, endDateTime }) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { routeId, driverId };
      if (vehicleId) body.vehicleId = vehicleId;
      if (endDateTime) body.endDateTime = endDateTime;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/ClaimRoute`, body);
      return response.data;
    } catch (error) {
      // Endpoint not deployed on this fleet (see getClaimableRoutes note).
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'Route bidding is not available on this fleet.' };
      }
      console.error('Error claiming route:', error);
      throw error;
    }
  },

  // Auto-assign optimized trips into a route the driver just claimed.
  // POST /API/Driver/AssignRouteTrips { routeId, driverId, vehicleId?, serviceDate?, optimizationType?, mode?, dryRun? }
  // The optimization (best-fit trips by least time/distance, within the shift &
  // capacity constraints) runs SERVER-SIDE — the app only requests it and renders
  // the result. The response mirrors getDriverSchedule's shape. LIVE action: like
  // claimRoute, never queued offline. See docs/ASSIGN_ROUTE_TRIPS_API_CONTRACT.md.
  assignRouteTrips: async ({ routeId, driverId, vehicleId, serviceDate, optimizationType, mode, dryRun } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { routeId, driverId };
      if (vehicleId) body.vehicleId = vehicleId;
      if (serviceDate) body.serviceDate = serviceDate;
      if (optimizationType) body.optimizationType = optimizationType;
      if (mode) body.mode = mode;
      if (dryRun != null) body.dryRun = dryRun;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/AssignRouteTrips`, body);
      return response.data;
    } catch (error) {
      // Endpoint not deployed on this fleet yet — degrade gracefully so the claim
      // still succeeds and dispatch can assign trips manually (no raw 404 shown).
      if (error?.response?.status === 404) {
        return { status: false, available: false, message: 'Auto-assign is not available on this fleet.' };
      }
      console.error('Error assigning route trips:', error);
      throw error;
    }
  },

  // #5 Release a route — clears Vehicle/Driver from the route.
  // POST /API/unassignVehicleFromRoute { routeId?, vehicleId? } (at least one)
  unassignVehicleFromRoute: async (routeId, vehicleId) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = {};
      if (routeId) body.routeId = routeId;
      if (vehicleId) body.vehicleId = vehicleId;
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/unassignVehicleFromRoute`, body);
      return response.data;
    } catch (error) {
      console.error('Error unassigning vehicle from route:', error);
      throw error;
    }
  },

  // #6 Update shift odometer (beginning and/or ending). At least one numeric ≥ 0.
  // POST /API/Driver/Odometer { shiftId, beginningOdometer?, endingOdometer? }
  updateShiftOdometer: async (shiftId, { beginningOdometer, endingOdometer } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const body = { shiftId };
      if (beginningOdometer != null) body.beginningOdometer = Number(beginningOdometer);
      if (endingOdometer != null) body.endingOdometer = Number(endingOdometer);
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Odometer`, body);
      return response.data;
    } catch (error) {
      console.error('Error updating shift odometer:', error);
      throw error;
    }
  },

  // #8 HTTP fallback for driver→dispatch pipe-delimited messages when socket is down.
  // POST /API/Driver/Dispatch { msg: "UNIT01:msgId|COMMAND|payload" }
  sendDispatchMessage: async (msg) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/Dispatch`, { msg });
      return response.data;
    } catch (error) {
      console.error('Error sending dispatch message:', error);
      throw error;
    }
  },

  // Driver SMS — send an SMS to the driver via the fleet gateway.
  // POST /API/Driver/SMS { apiKey, driverNumber, message, updates? }
  // driverNumber defaults to the logged-in driver; apiKey is pulled from fleetSettings
  // when not supplied. `updates` (string "true"/"false") opts into status-update texts.
  sendDriverSMS: async (message, { driverNumber, updates } = {}) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const userSettings = JSON.parse(localStorage.getItem('userSettings')) || {};
      const body = {
        apiKey: fleetSettings.apiKey || fleetSettings.apiToken,
        driverNumber:
          driverNumber || userSettings.Code || userSettings.code || userSettings.driverNumber,
        message,
      };
      if (updates != null) body.updates = String(updates);
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/SMS`, body);
      return response.data;
    } catch (error) {
      console.error('Error sending driver SMS:', error);
      throw error;
    }
  },

  // VIN OCR — upload a photo of the VIN plate and get the decoded VIN string.
  // POST /API/Driver/getVin  (multipart/form-data): file upload field + form field
  // `image` = base64 JPG. Accepts a File/Blob, a base64 data-URL/string, or both.
  getVin: async (fileOrBase64, base64Image) => {
    try {
      const fleetSettings = JSON.parse(localStorage.getItem('fleetSettings')) || {};
      if (!fleetSettings.fleetUrl) throw new Error('Fleet settings not available (no fleetUrl)');
      const form = new FormData();
      let base64 = base64Image;
      if (fileOrBase64 instanceof Blob) {
        form.append('file', fileOrBase64, fileOrBase64.name || 'vin.jpg');
      } else if (typeof fileOrBase64 === 'string') {
        // Caller passed the base64 string directly as the first arg.
        base64 = base64 || fileOrBase64;
      }
      if (base64) form.append('image', base64);
      const response = await axios.post(`${fleetSettings.fleetUrl}/API/Driver/getVin`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error scanning VIN:', error);
      throw error;
    }
  },
};

// Dev-only console hook: expose comms (and the mock module) on window so the
// API methods can be exercised from DevTools in development. Stripped from
// production builds (NODE_ENV === 'production'); no effect on shipped app.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.comms = comms;
}

export default comms;